import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { tokenManager } from "@/utils/tokenManager";
import { deploymentApi, usageApi } from "@/services/omnimizer-api-service";

const RAG_API_URL =
  window.env?.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

const statsApi = axios.create({ baseURL: RAG_API_URL });
tokenManager.setupInterceptors(statsApi);

export type StatsFilters = {
  startDate: Date;
  endDate: Date;
  userIds?: string[];
  projectIds?: string[];
  balanceId?: string | null;
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Serializes a Date as a naive ISO-8601 datetime string in UTC (no timezone
 * suffix), e.g. "2026-04-22T10:30:00". The backend treats naive datetimes as UTC
 * and compares them against UTC billing periods, so the wall-clock we emit must be
 * the UTC one - using local getHours() here would offset every window by the
 * viewer's timezone and push it outside the billing period (0% cost for any
 * non-UTC user).
 */
const toNaiveDateTime = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
  `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;

const buildParams = (filters: StatsFilters) => {
  const params = new URLSearchParams();
  params.set("start_date", toNaiveDateTime(filters.startDate));
  params.set("end_date", toNaiveDateTime(filters.endDate));
  filters.userIds
    ?.filter(Boolean)
    .forEach((id) => params.append("user_ids", id));
  filters.projectIds
    ?.filter(Boolean)
    .forEach((id) => params.append("project_ids", id));
  if (filters.balanceId) params.set("balance_id", filters.balanceId);
  return params;
};

const filterKey = (f: StatsFilters) => ({
  start: toNaiveDateTime(f.startDate),
  end: toNaiveDateTime(f.endDate),
  users: f.userIds ?? [],
  projects: f.projectIds ?? [],
  balance: f.balanceId ?? null,
});

export type RequestStats = {
  total_requests: number;
  success_requests: number;
  failed_requests: number;
};

export type TokenStats = {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_cost: number;
};

export type UsageStats = {
  total_runtime: number;
  total_runtime_cost: number;
  total_queries: number;
  total_queries_cost: number;
  limits?: {
    total_runtime?: number;
    total_runtime_cost?: number;
    total_queries?: number;
    total_queries_cost?: number;
  };
};

export type SpendStats = { total_spend: number; org_balance: number };

export type TokensOverTimePoint = {
  date: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type RequestsOverTimePoint = {
  date: string;
  total: number;
  successful: number;
  failed: number;
};

export type DailySpendPoint = {
  date: string;
  models: number;
  flows: number;
  rags: number;
  api_calls: number;
  deployments?: number;
};

export type TopApiKey = {
  api_key_id: string;
  api_key_name: string;
  user_id: string;
  total_calls: number;
  total_spend: number;
};

export type TopModel = {
  model: string;
  provider: string | null;
  total_spend: number;
  successful_requests: number;
  failed_requests: number;
};

export type TopFlow = {
  flow_id: string;
  flow_name: string;
  user_id: string;
  total_runs: number;
  total_running_time_ms: number;
  total_spend: number;
};

export type TopDeployment = {
  deployment_id: string;
  deployment_name: string;
  total_requests: number;
  gpu_hours: number;
  total_spend: number;
};

export type GpuComposition = {
  usage: {
    quota: OrganizationQuota | null;
    deployments: TopDeployment[];
  } | null;
  usage_available: boolean;
};

type GpuCompositionResponse = {
  usage: {
    quota: OrganizationQuota | null;
    deployments: Array<{
      deployment_id: string;
      deployment_name: string;
      total_requests: number | string;
      total_gpu_hours?: number | string | null;
      total_hours?: number | string | null;
      spend: number | string;
    }>;
  } | null;
  usage_available: boolean;
};

const toFiniteNumber = (value: number | string | null | undefined) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizeGpuComposition = (
  response: GpuCompositionResponse,
): GpuComposition => ({
  ...response,
  usage: response.usage
    ? {
        ...response.usage,
        deployments: response.usage.deployments.map((deployment) => ({
          deployment_id: deployment.deployment_id,
          deployment_name: deployment.deployment_name,
          total_requests: toFiniteNumber(deployment.total_requests),
          gpu_hours: toFiniteNumber(
            deployment.total_gpu_hours ?? deployment.total_hours ?? 0,
          ),
          total_spend: toFiniteNumber(deployment.spend),
        })),
      }
    : null,
});

export type TopProject = {
  project_id: string;
  project_name: string;
  total_spend: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type TopProvider = {
  provider: string | null;
  total_spend: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type LlmConfigType =
  | "chat"
  | "multimodal"
  | "embedding"
  | "speech_to_text"
  | "parsing"
  | "text_to_speech"
  | "image_generation";

export type LlmConfigSource = "user" | "public";

export type LlmConfigRow = {
  id: string;
  provider: string | null;
  model: string;
  type: LlmConfigType | string;
  requests: number;
  spend: number;
};

export type LlmConfigOrderBy =
  | "provider"
  | "model"
  | "type"
  | "requests"
  | "spend";

export type LlmConfigsParams = {
  providers?: string[];
  sources?: LlmConfigSource[];
  types?: string[];
  search?: string;
  orderBy?: LlmConfigOrderBy;
  orderDir?: "asc" | "desc";
  page: number;
  pageSize: number;
};

export type LlmConfigsResponse = {
  data: LlmConfigRow[];
  metadata: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
};

export type LookupItem = { value: string; label: string };
export type PlanInfo = {
  code: string;
  name: string;
  expires_at: string | null;
  period_start: string | null;
  period_end: string | null;
};

export type OrganizationQuota = {
  used: number;
  total: number;
  available: number;
  period_start: string;
  period_end: string;
  seconds_until_reset: number;
};

const fetchStats = async <T>(
  path: string,
  params: URLSearchParams,
): Promise<T> => {
  const { data } = await statsApi.get<T>(path, { params });
  return data;
};

const fetchList = async <T>(
  path: string,
  params: URLSearchParams,
): Promise<T[]> => {
  const { data } = await statsApi.get<{ data: T[] }>(path, { params });
  return data.data ?? [];
};

export function useRequestStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "requests", filterKey(filters)],
    queryFn: () =>
      fetchStats<RequestStats>("/stats/requests/", buildParams(filters)),
  });
}

export function useTokenStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "tokens", filterKey(filters)],
    queryFn: () =>
      fetchStats<TokenStats>("/stats/tokens/", buildParams(filters)),
  });
}

export function useUsageStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "usage", filterKey(filters)],
    queryFn: () =>
      fetchStats<UsageStats>("/stats/usage/", buildParams(filters)),
  });
}

export function useSpendStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "spend", filterKey(filters)],
    queryFn: () =>
      fetchStats<SpendStats>("/stats/spend/", buildParams(filters)),
  });
}

export function useTokensOverTime(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "tokens-over-time", filterKey(filters)],
    queryFn: () =>
      fetchList<TokensOverTimePoint>(
        "/stats/tokens/over-time",
        buildParams(filters),
      ),
  });
}

export function useRequestsOverTime(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "requests-over-time", filterKey(filters)],
    queryFn: () =>
      fetchList<RequestsOverTimePoint>(
        "/stats/requests-over-time/",
        buildParams(filters),
      ),
  });
}

export function useDailySpend(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "daily-spend", filterKey(filters)],
    queryFn: () =>
      fetchList<DailySpendPoint>("/stats/daily-spend/", buildParams(filters)),
  });
}

type TopParams = StatsFilters & { limit: number; sortBy: string };

const buildTopParams = (filters: TopParams) => {
  const params = buildParams(filters);
  params.set("sort_by", filters.sortBy);
  params.set("limit", String(filters.limit));
  return params;
};

export function useTopApiKeys(filters: StatsFilters, limit: number) {
  return useQuery({
    queryKey: ["stats", "top-api-keys", limit, filterKey(filters)],
    queryFn: () =>
      fetchList<TopApiKey>(
        "/stats/api-keys/top",
        buildTopParams({ ...filters, limit, sortBy: "total_spend" }),
      ),
  });
}

export function useTopModels(filters: StatsFilters, limit: number) {
  return useQuery({
    queryKey: ["stats", "top-models", limit, filterKey(filters)],
    queryFn: () =>
      fetchList<TopModel>(
        "/stats/models/top",
        buildTopParams({ ...filters, limit, sortBy: "total_spend" }),
      ),
  });
}

export function useTopFlows(filters: StatsFilters, limit: number) {
  return useQuery({
    queryKey: ["stats", "top-flows", limit, filterKey(filters)],
    queryFn: () =>
      fetchList<TopFlow>(
        "/stats/flows/top",
        buildTopParams({ ...filters, limit, sortBy: "total_running_time_ms" }),
      ),
  });
}

export function useGpuStats(
  filters: StatsFilters,
  organizationId: string | null,
  limit: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ["stats", "gpu", organizationId, limit, filterKey(filters)],
    queryFn: async ({ signal }) => {
      const params = buildParams(filters);
      params.delete("balance_id");
      params.set("limit", String(limit));
      const { data } = await statsApi.get<GpuCompositionResponse>(
        "/stats/gpu/",
        {
          params,
          signal,
          headers: { "X-Organization-ID": organizationId || undefined },
        },
      );
      return normalizeGpuComposition(data);
    },
    enabled,
  });
}

export type GpuStatsQuery = ReturnType<typeof useGpuStats>;

export function useTopProviders(filters: StatsFilters, limit = 10) {
  return useQuery({
    queryKey: ["stats", "top-providers", limit, filterKey(filters)],
    queryFn: () =>
      fetchList<TopProvider>(
        "/stats/providers/top",
        buildTopParams({ ...filters, limit, sortBy: "total_spend" }),
      ),
  });
}

export function useLlmConfigs(filters: StatsFilters, params: LlmConfigsParams) {
  return useQuery({
    queryKey: [
      "stats",
      "llm-configs",
      filterKey(filters),
      params.providers ?? [],
      params.sources ?? [],
      params.types ?? [],
      params.search ?? "",
      params.orderBy ?? "spend",
      params.orderDir ?? "desc",
      params.page,
      params.pageSize,
    ],
    queryFn: async () => {
      const query = buildParams(filters);
      params.providers
        ?.filter(Boolean)
        .forEach((p) => query.append("providers", p));
      params.sources
        ?.filter(Boolean)
        .forEach((s) => query.append("sources", s));
      params.types?.filter(Boolean).forEach((t) => query.append("types", t));
      if (params.search) query.set("search", params.search);
      query.set("order_by", params.orderBy ?? "spend");
      query.set("order_dir", params.orderDir ?? "desc");
      query.set("page", String(params.page));
      query.set("page_size", String(params.pageSize));
      const { data } = await statsApi.get<LlmConfigsResponse>(
        "/stats/llm-configs/",
        { params: query },
      );
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useUsersLookup(enabled = true) {
  const organizationId =
    typeof window !== "undefined"
      ? localStorage.getItem("currentOrganizationId")
      : null;

  return useQuery({
    queryKey: ["stats", "lookup", "users", organizationId],
    queryFn: async () => {
      const { data } = await statsApi.get<{ data: LookupItem[] }>(
        "/stats/lookup/users",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data.data ?? [];
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTopProjects(filters: StatsFilters, limit = 5) {
  return useQuery({
    queryKey: ["stats", "top-projects", limit, filterKey(filters)],
    queryFn: () =>
      fetchList<TopProject>(
        "/stats/projects/top",
        buildTopParams({ ...filters, limit, sortBy: "total_spend" }),
      ),
  });
}

export function useProjectsLookup(enabled = true) {
  const organizationId =
    typeof window !== "undefined"
      ? localStorage.getItem("currentOrganizationId")
      : null;

  return useQuery({
    queryKey: ["stats", "lookup", "projects", organizationId],
    queryFn: async () => {
      const { data } = await statsApi.get<{ data: LookupItem[] }>(
        "/stats/lookup/projects",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data.data ?? [];
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrganizationSubscription(organizationId?: string | null) {
  return useQuery({
    queryKey: [
      "usage",
      "plans",
      "organization",
      "subscription",
      organizationId,
    ],
    queryFn: async () => {
      const { data } = await statsApi.get<PlanInfo>(
        "/usage/plans/organization/subscription",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data;
    },
    enabled: Boolean(organizationId),
  });
}

export function useOrganizationQuota(organizationId?: string | null) {
  return useQuery({
    queryKey: ["usage", "organization", organizationId, "quota", "GPU_HOURS"],
    queryFn: async () => {
      const { data } = await usageApi.getOrganizationQuota(
        organizationId,
        "GPU_HOURS",
      );
      return data as OrganizationQuota;
    },
    enabled: Boolean(organizationId),
  });
}

export function useUserPlan(enabled = true) {
  return useQuery({
    queryKey: ["usage", "plans", "user"],
    queryFn: async () => {
      const { data } = await statsApi.get<PlanInfo>("/usage/plans/user");
      return data;
    },
    enabled,
  });
}
